import type { AgingInvoice } from '../content/seed-invoices';
import { SEED_INVOICES } from '../content/seed-invoices';
import type { EventBus, EventPayloads } from './EventBus';

/** Mutable AR sub-ledger. Seeds from SEED_INVOICES at construction; closes
 *  invoices as the player applies cash, writes them off, etc. The aging
 *  spreadsheet, dunning modal, and cash-app modal all read from this so
 *  the AR view is consequential — applying a wire visibly reduces open
 *  balance, the next CFO question reflects the new reality. */
export class ARLedger {
  private invoices: AgingInvoice[];

  private readonly onCashApplied = (p: EventPayloads['cash-application:sent']): void => {
    if (!p.correctCustomer) return;
    // Each allocation closes (drops) that invoice. v1: full close on any
    // allocation; partial-pay tracking is a later refinement.
    const closed = new Set(p.allocations.map((a) => a.invoiceId));
    if (closed.size === 0) return;
    this.invoices = this.invoices.filter((i) => !closed.has(i.id));
    this.bus.emit('ar-ledger:changed', { reason: 'cash-applied' });
  };

  private readonly onWriteOff = (p: EventPayloads['write-off:approved']): void => {
    this.invoices = this.invoices.filter((i) => i.id !== p.invoiceId);
    this.bus.emit('ar-ledger:changed', { reason: 'write-off' });
  };

  constructor(private readonly bus: EventBus) {
    this.invoices = SEED_INVOICES.map((i) => ({ ...i }));
    bus.on('cash-application:sent', this.onCashApplied);
    bus.on('write-off:approved', this.onWriteOff);
  }

  get current(): readonly AgingInvoice[] {
    return this.invoices;
  }

  destroy(): void {
    this.bus.off('cash-application:sent', this.onCashApplied);
    this.bus.off('write-off:approved', this.onWriteOff);
  }
}
